import React, { useState, useEffect } from 'react';
import '../styles/PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  const [visibleSections, setVisibleSections] = useState<number[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const sections = document.querySelectorAll('.policy-section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            if (!visibleSections.includes(index)) {
              setVisibleSections(prev => [...prev, index]);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    sections.forEach((section, index) => {
      section.setAttribute('data-index', index.toString());
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, [visibleSections]);

  // Dark mode detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <div className="privacy-policy-page">
      {/* Header */}
      <header className="policy-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <img 
                src="/favicon-32x32-white.png" 
                alt="GordianRFQ Logo" 
                className="logo-icon"
              />
              <a href="/" className="logo-text">RFQ Agent</a>
            </div>
          </div>
          <nav className="header-nav">
            <button className="nav-link" onClick={() => window.location.href = '/'}>
              Home
            </button>
            <button className="nav-link" onClick={() => window.location.href = '/#pricing'}>
              Pricing
            </button>
            <button className="nav-link" onClick={() => window.location.href = '/#about'}>
              About
            </button>
            <button className="nav-link" onClick={() => window.location.href = '/#contact'}>
              Contact
            </button>
            <button className="login-btn" onClick={() => window.location.href = '/signin'}>
              Sign In
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="policy-main">
        <div className="policy-container">
          <h1 className="policy-title">Privacy Policy for Gordian Analytics SA</h1>
          
          <div className="policy-content">
            <div className={`policy-section ${visibleSections.includes(0) ? 'visible' : ''}`}>
              <h2>1. Purpose of this Privacy Policy</h2>
              <p>
                Gordian Analytics SA ("we", "us", "our") values your privacy and is committed to protecting your personal data. 
                This Privacy Policy explains how we collect, process, and use your data when you use our website, services, and software. 
                This policy is designed to comply with the Swiss Federal Act on Data Protection (FADP/DSG) and, where applicable, 
                the EU General Data Protection Regulation (GDPR).
              </p>
            </div>

            <div className={`policy-section ${visibleSections.includes(1) ? 'visible' : ''}`}>
              <h2>2. Controller Information</h2>
              <p>
                <strong>Gordian Analytics SA</strong><br/>
                [Avenue de France],<br/>
                1004 Lausanne, Switzerland<br/>
                Email: <a href="mailto:info@gordiananalytics.com">info@gordiananalytics.com</a><br/><br/>
                We are the data controller responsible for your personal data under this policy.
              </p>
            </div>

            <div className={`policy-section ${visibleSections.includes(2) ? 'visible' : ''}`}>
              <h2>3. Data We Process</h2>
              <p>We may process the following categories of personal data:</p>
              <ul>
                <li><strong>Technical Data:</strong> IP address, browser type, access times, referring URLs.</li>
                <li><strong>Registration Data:</strong> Information provided when creating accounts or using our services.</li>
                <li><strong>Communication Data:</strong> Records of communications via email, phone, or other channels.</li>
                <li><strong>Master Data:</strong> Name, contact information, company name, role/function.</li>
                <li><strong>Contractual Data:</strong> Details related to services rendered, agreements, and transactions.</li>
              </ul>
            </div>

            <div className={`policy-section ${visibleSections.includes(3) ? 'visible' : ''}`}>
              <h2>4. Purpose of Processing</h2>
              <p>We process your data to:</p>
              <ul>
                <li>Communicate with you and respond to inquiries.</li>
                <li>Provide, manage, and improve our services and software.</li>
                <li>Fulfill contractual obligations.</li>
                <li>Comply with legal requirements.</li>
                <li>Conduct internal research and analytics.</li>
                <li>Protect our rights and prevent fraud.</li>
              </ul>
            </div>

            <div className={`policy-section ${visibleSections.includes(4) ? 'visible' : ''}`}>
              <h2>5. Legal Basis for Processing</h2>
              <p>We process your data based on:</p>
              <ul>
                <li>Your consent (Art. 6(1)(a) GDPR);</li>
                <li>Contractual necessity (Art. 6(1)(b) GDPR);</li>
                <li>Legal obligations (Art. 6(1)(c) GDPR);</li>
                <li>Legitimate interests (Art. 6(1)(f) GDPR), such as improving our services and ensuring IT security.</li>
              </ul>
            </div>

            <div className={`policy-section ${visibleSections.includes(5) ? 'visible' : ''}`}>
              <h2>6. Data Sharing</h2>
              <p>We may share your data with:</p>
              <ul>
                <li>Service providers (e.g. IT hosting, payment processing);</li>
                <li>Legal authorities, when required;</li>
                <li>Business partners, in the context of delivering services.</li>
              </ul>
              <p>All third parties are contractually obliged to respect the confidentiality and security of your data.</p>
            </div>

            <div className={`policy-section ${visibleSections.includes(6) ? 'visible' : ''}`}>
              <h2>7. Data Transfers Abroad</h2>
              <p>
                Your data may be transferred outside of Switzerland and the EEA. In such cases, 
                we ensure adequate protection through standard contractual clauses or reliance on legal exceptions.
              </p>
            </div>

            <div className={`policy-section ${visibleSections.includes(7) ? 'visible' : ''}`}>
              <h2>8. Retention of Data</h2>
              <p>
                We retain your data as long as required for the purposes for which it was collected, 
                and for legal or operational reasons. Communication and contractual data may be retained up to 10 years.
              </p>
            </div>

            <div className={`policy-section ${visibleSections.includes(8) ? 'visible' : ''}`}>
              <h2>9. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal data 
                from unauthorized access, loss, or misuse.
              </p>
            </div>

            <div className={`policy-section ${visibleSections.includes(9) ? 'visible' : ''}`}>
              <h2>10. Your Rights</h2>
              <p>You have the right to:</p>
              <ul>
                <li>Access and correct your personal data;</li>
                <li>Request deletion or restriction of processing;</li>
                <li>Object to data processing based on legitimate interest;</li>
                <li>Withdraw consent at any time;</li>
                <li>Lodge a complaint with a data protection authority.</li>
              </ul>
              <p>To exercise these rights, contact us at <a href="mailto:info@gordiananalytics.com">info@gordiananalytics.com</a>.</p>
            </div>

            <div className={`policy-section ${visibleSections.includes(10) ? 'visible' : ''}`}>
              <h2>11. Cookies and Tracking</h2>
              <p>
                We use cookies and similar technologies to improve your experience. 
                You can manage your preferences through your browser settings. 
                For more, refer to our Cookie Policy.
              </p>
            </div>

            <div className={`policy-section ${visibleSections.includes(11) ? 'visible' : ''}`}>
              <h2>12. Updates to this Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. The current version is published on our website 
                and replaces previous versions. Last updated: <strong>05.2025</strong>
              </p>
            </div>

            <div className={`policy-section ${visibleSections.includes(12) ? 'visible' : ''}`}>
              <p className="copyright">© 2025 Gordian Analytics. All rights reserved.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
